import { useMemo } from 'react';

import { MediaClient } from '@atlaskit/media-client/media-client';
import type { MediaClientConfig } from '@atlaskit/media-client/media-core';

import { useAuthProvider } from './useAuthProvider.ts';

export type UseMediaClientConfig = {
	contentId: string | undefined;
	onMediaSessionError?: (err: Error) => void;
	config?: Partial<MediaClientConfig>;
	/**
	 * When true, fetches a read-only media token instead of a read-write upload token.
	 * Use this for view-only contexts (e.g. rendering existing attachments) to avoid
	 * failing for users who lack upload attachment permissions.
	 */
	readOnly?: boolean;
};

const defaultMediaClientConfig: Partial<MediaClientConfig> = {
	useSha256ForUploads: true,
};

export const useMediaClient = ({
	contentId,
	onMediaSessionError,
	config,
	readOnly,
}: UseMediaClientConfig): MediaClient | null => {
	const authProvider = useAuthProvider({ contentId, onMediaSessionError, readOnly });

	return useMemo(() => {
		if (!authProvider) {
			return null;
		}

		const mediaClientConfig: MediaClientConfig = {
			authProvider,
			...defaultMediaClientConfig,
			...config,
		};

		return new MediaClient(mediaClientConfig);
	}, [authProvider, config]);
};
